// ─────────────────────────────────────────────────────────────────────────
// PermissionContext — resolves the logged-in user's CURRENT role permissions
// from GET /me/permissions.
//
// `menuPermission` in localStorage is still maintained because several legacy
// consumers (Sidebar and app-specific helpers) read that shape directly. The
// live API response is authoritative and is mirrored back into localStorage so
// a browser refresh/token refresh cannot fall back to an old login snapshot.
//
// Shape of `permissions`: moduleName -> actionKey -> boolean, or the wildcard
// { "*": { "*": true } } for full-access roles (Admin/Manager).
// ─────────────────────────────────────────────────────────────────────────
import { createContext, useCallback, useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { getMyPermissions } from '../services/adminApi';
import { getStoredToken } from '../services/authApi';

export const PermissionContext = createContext({ permissions: {}, loading: true });

function isFullAccess(permissions) {
  return Boolean(permissions?.['*']?.['*']);
}

function toLegacyMenuPermission(permissions) {
  if (!permissions || typeof permissions !== 'object' || Array.isArray(permissions)) return {};

  return Object.fromEntries(
    Object.entries(permissions)
      .filter(([moduleName]) => moduleName !== '*')
      .map(([moduleName, actions]) => [
        moduleName,
        Object.fromEntries(
          Object.entries(actions ?? {}).map(([action, allowed]) => [action, allowed ? 1 : 0]),
        ),
      ]),
  );
}

function syncLegacyPermissionSnapshot(permissions) {
  // Full-access roles intentionally use a wildcard in /me/permissions. Keep the
  // richer login-time menuPermission map for legacy helpers; replacing it with
  // only {"*":{"*":1}} would make helpers that do not understand wildcards
  // (for example app-specific sendApprove checks) incorrectly return false.
  if (isFullAccess(permissions)) return;

  try {
    localStorage.setItem('menuPermission', JSON.stringify(toLegacyMenuPermission(permissions)));
    window.dispatchEvent(new Event('menu:updated'));
  } catch {
    // Storage can be unavailable in private/locked-down browser contexts. The
    // live React permission state still remains usable, so do not fail access
    // resolution merely because the compatibility snapshot could not be saved.
  }
}

export function PermissionProvider({ children }) {
  const [permissions, setPermissions] = useState({});
  const [loading, setLoading] = useState(true);

  const refresh = useCallback(() => {
    let hasToken = false;
    try {
      hasToken = Boolean(getStoredToken());
    } catch {
      hasToken = false;
    }
    if (!hasToken) {
      setPermissions({});
      setLoading(false);
      return;
    }

    setLoading(true);
    getMyPermissions()
      .then((data) => {
        const next = data ?? {};
        setPermissions(next);
        syncLegacyPermissionSnapshot(next);
      })
      // Keep the existing login snapshot as the compatibility fallback when the
      // live permission endpoint is temporarily unavailable. Clearing it here
      // would make a transient network error look like a permission change.
      .catch(() => setPermissions({}))
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => {
    refresh();
    window.addEventListener('auth:login', refresh);
    window.addEventListener('auth:logout', refresh);
    window.addEventListener('auth:token-refreshed', refresh);
    window.addEventListener('permissions:changed', refresh);
    window.addEventListener('storage', refresh);
    return () => {
      window.removeEventListener('auth:login', refresh);
      window.removeEventListener('auth:logout', refresh);
      window.removeEventListener('auth:token-refreshed', refresh);
      window.removeEventListener('permissions:changed', refresh);
      window.removeEventListener('storage', refresh);
    };
  }, [refresh]);

  return (
    <PermissionContext.Provider value={{ permissions, loading, refresh }}>
      {children}
    </PermissionContext.Provider>
  );
}

PermissionProvider.propTypes = {
  children: PropTypes.node,
};
